import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';

export interface CopyOptions {
  successMessage?: string;
  errorMessage?: string;
}

export const useCopyToClipboard = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const copyText = useCallback(
    async (text: string, options?: CopyOptions): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
        enqueueSnackbar(options?.successMessage ?? t('Copied to clipboard'), {
          variant: 'success',
        });
        return true;
      } catch {
        enqueueSnackbar(options?.errorMessage ?? t('Could not copy to clipboard'), {
          variant: 'error',
        });
        return false;
      }
    },
    [enqueueSnackbar, t],
  );

  const copyBlob = useCallback(
    async (
      blobOrPromise: Blob | Promise<Blob>,
      mimeType: string,
      options?: CopyOptions,
    ): Promise<boolean> => {
      try {
        const ClipboardItemCtor = (window as unknown as { ClipboardItem?: typeof ClipboardItem })
          .ClipboardItem;
        if (!ClipboardItemCtor || !navigator.clipboard?.write) {
          throw new Error('ClipboardItem unsupported');
        }
        const typedBlobPromise = Promise.resolve(blobOrPromise).then((blob) =>
          blob.type === mimeType ? blob : new Blob([blob], { type: mimeType }),
        );
        await navigator.clipboard.write([new ClipboardItemCtor({ [mimeType]: typedBlobPromise })]);
        enqueueSnackbar(options?.successMessage ?? t('Copied to clipboard'), {
          variant: 'success',
        });
        return true;
      } catch {
        enqueueSnackbar(options?.errorMessage ?? t('Could not copy to clipboard'), {
          variant: 'error',
        });
        return false;
      }
    },
    [enqueueSnackbar, t],
  );

  const copyImageAsPng = useCallback(
    async (blobOrPromise: Blob | Promise<Blob>, options?: CopyOptions): Promise<boolean> => {
      try {
        const ClipboardItemCtor = (window as unknown as { ClipboardItem?: typeof ClipboardItem })
          .ClipboardItem;
        if (!ClipboardItemCtor || !navigator.clipboard?.write) {
          throw new Error('ClipboardItem unsupported');
        }
        const pngBlobPromise = Promise.resolve(blobOrPromise).then(toPngBlob);
        await navigator.clipboard.write([new ClipboardItemCtor({ 'image/png': pngBlobPromise })]);
        enqueueSnackbar(options?.successMessage ?? t('Image copied to clipboard'), {
          variant: 'success',
        });
        return true;
      } catch {
        enqueueSnackbar(options?.errorMessage ?? t('Could not copy the image'), {
          variant: 'error',
        });
        return false;
      }
    },
    [enqueueSnackbar, t],
  );

  const copyImage = useCallback(
    async (imageUrl: string, options?: CopyOptions): Promise<boolean> => {
      try {
        const blobPromise = fetch(imageUrl).then((r) => r.blob());
        return copyImageAsPng(blobPromise, options);
      } catch {
        return copyText(imageUrl, {
          successMessage: t('Image link copied to clipboard'),
          errorMessage: options?.errorMessage ?? t('Could not copy image'),
        });
      }
    },
    [copyImageAsPng, copyText, t],
  );

  return { copyText, copyImage, copyBlob, copyImageAsPng };
};

const toPngBlob = async (blob: Blob): Promise<Blob> => {
  if (blob.type === 'image/png') return blob;
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d context unavailable');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (png) => (png ? resolve(png) : reject(new Error('Canvas toBlob returned null'))),
      'image/png',
    );
  });
};
